/* istanbul ignore file */
import { useEffect, useRef } from 'react';
import { Loading } from 'framework/ui/mui-builder';
import HandbookMenu, { MAX_SM_WIDTH } from '../../shared/HandbookMenu/HandbookMenu';
import HandbookSubpagesListLayout from './HandbookSubpagesListLayout';
import { ControlledShow } from 'framework/show';
import useHandbookPageService from './handbookPageService';
import { scrollToHash } from 'framework/utils';
import HandbookSearchInput from '../../shared/input/HandbookSearchInput';
import { SectionWithScrollToHash } from 'framework/ui/layout';
import { Page404 } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import { useLocation } from 'react-router-dom';
import { Box, Paper, Stack } from '@mui/material';
import { ListHeader } from 'framework/list';
import { handbookContentSx } from './handbookContentStyles';
import { pageContentSx } from 'framework/ui/layout';

const HandbookPageLayout = () => {
  const { isAuthenticated } = useAuthenticationService();
  const { pageStructure, loading, content, tableContent, isSearchMode, searching } = useHandbookPageService();
  const ref = useRef<Nullable<HTMLDivElement>>(null);
  const { hash } = useLocation();

  useEffect(() => {
    const hashElement = hash && ref?.current?.querySelector && ref?.current?.querySelector(hash);

    if (!loading && ref?.current && !hashElement && window.innerWidth < MAX_SM_WIDTH) {
      scrollToHash(ref?.current);
    }
    // Suppressed warnings because we only need to call useEffect if page is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!isSearchMode && !loading && !content && !pageStructure?.subPages?.length) {
    return <Page404 isAuthenticated={isAuthenticated} />;
  }

  return (
    <ControlledShow data={pageStructure}>
      <Paper className="page-content" elevation={0} sx={pageContentSx}>
        <ListHeader label="Handbook" />

        <Box className="handbook-content">
          <HandbookSearchInput isSearchMode={isSearchMode} />
          {isSearchMode && searching && <Loading />}
          {!isSearchMode && (
            <Stack
              sx={theme => ({
                flexDirection: 'column',
                paddingBottom: '20px',
                minHeight: '300px',
                [theme.breakpoints.up('lg')]: {
                  flexDirection: 'row',
                },
              })}
            >
              <Box
                sx={
                  loading
                    ? undefined
                    : theme => ({
                        [theme.breakpoints.up('lg')]: {
                          width: '285px',
                          flexShrink: 0,
                          marginRight: '45px',
                        },
                      })
                }
              >
                <aside id="js-left-column">
                  <HandbookMenu tableContent={loading ? [] : tableContent} />
                </aside>
              </Box>
              <Box
                sx={theme => ({
                  width: '100%',
                  minHeight: '200px',
                  position: 'relative',
                  [theme.breakpoints.up('lg')]: {
                    maxWidth: 'calc(100% - 330px)',
                  },
                })}
              >
                {loading ? (
                  <Loading />
                ) : (
                  <SectionWithScrollToHash isScrollActive={!loading}>
                    <Box ref={ref} sx={handbookContentSx}>
                      {!content && pageStructure?.subPages?.length ? (
                        <HandbookSubpagesListLayout pageStructure={pageStructure} />
                      ) : (
                        content
                      )}
                    </Box>
                  </SectionWithScrollToHash>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>
    </ControlledShow>
  );
};

export default HandbookPageLayout;
